import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { ExpandableCard } from '@/components/common';
import { DROPDOWN_MRU_SELECTORS, useDropdownMru } from '@/hooks/useDropdownMru';
import { useServices, type Service } from '@/hooks/useServices';
import { useMechanics } from '@/hooks/useStaffUsers';
import { ServiceCatalogSelect } from './ServiceCatalogSelect';

interface AddServiceRowProps {
  onAdd: (serviceId: string, mechanicId?: string | null) => void | Promise<void>;
  adding: boolean;
}

export function AddServiceRow({
  onAdd,
  adding,
}: AddServiceRowProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const { data: availableServices = [], isLoading } = useServices({ isActive: true, sortBy: 'usage' });
  const { data: mechanics = [] } = useMechanics();
  const mechanicMru = useDropdownMru(DROPDOWN_MRU_SELECTORS.mechanic);
  const [expanded, setExpanded] = useState(false);
  const [selected, setSelected] = useState<Service | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [selectedMechanicId, setSelectedMechanicId] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const mechanicLabelId = useId();

  const resetFields = () => {
    setSelected(null);
    setInputValue('');
    setSelectedMechanicId('');
  };

  const focusSearch = () => {
    panelRef.current?.querySelector<HTMLInputElement>('input')?.focus();
  };

  const handleToggle = () => {
    setExpanded((open) => !open);
  };

  useEffect(() => {
    if (!expanded) return;
    const timeoutId = window.setTimeout(focusSearch, 0);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [expanded]);

  const handleAdd = async () => {
    if (!selected) return;
    try {
      await onAdd(selected.id, selectedMechanicId || null);
    } catch {
      return;
    }
    resetFields();
    window.setTimeout(focusSearch, 0);
  };

  return (
    <ExpandableCard
      expanded={expanded}
      // Title button is the single disclosure control. ExpandableCard's
      // header IconButton has no accessible name (#2656).
      expandable={false}
      title={
        <ButtonBase
          type="button"
          onClick={handleToggle}
          aria-expanded={expanded}
          aria-controls={panelId}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            width: '100%',
            justifyContent: 'flex-start',
            textAlign: 'left',
            borderRadius: 1,
            '&.Mui-focusVisible': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: 2,
            },
          }}
        >
          <AddIcon fontSize="small" color="primary" />
          <Typography
            variant="subtitle2"
            fontWeight={600}
            color="primary"
            component="span"
            sx={{ flex: 1 }}
          >
            {t('detail.addService')}
          </Typography>
          {expanded
            ? <ExpandLessIcon fontSize="small" />
            : <ExpandMoreIcon fontSize="small" />}
        </ButtonBase>
      }
    >
      <Box
        ref={panelRef}
        id={panelId}
        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <ServiceCatalogSelect
          options={availableServices}
          loading={isLoading}
          value={selected}
          onChange={setSelected}
          inputValue={inputValue}
          onInputChange={(next, reason) => {
            // MUI emits `reset`/`blur` (clearOnBlur) after value changes and
            // on leaving the field. Applying them would wipe a typed query
            // before Add, or restore the previous label as a ghost (#2885).
            if (reason === 'reset' || reason === 'blur') return;
            setInputValue(next);
          }}
          label={t('create.serviceType')}
          placeholder={t('create.selectServicesPlaceholder')}
          size="small"
        />

        {selected && (
          <FormControl fullWidth size="small">
            <InputLabel id={mechanicLabelId}>{t('detail.assignedMechanic')}</InputLabel>
            <Select
              labelId={mechanicLabelId}
              value={selectedMechanicId}
              onChange={(event) => {
                const next = event.target.value;
                setSelectedMechanicId(next);
                if (next !== '') mechanicMru.record(next);
              }}
              label={t('detail.assignedMechanic')}
            >
              <MenuItem value="">
                <em>{t('detail.noMechanic')}</em>
              </MenuItem>
              {mechanicMru.order(mechanics, (mechanic) => mechanic.id).map((mechanic) => (
                <MenuItem key={mechanic.id} value={mechanic.id}>
                  {mechanic.firstName} {mechanic.lastName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            size="small"
            onClick={() => { void handleAdd(); }}
            disabled={!selected || adding}
          >
            {adding ? <CircularProgress size={20} /> : tc('add')}
          </Button>
        </Box>
      </Box>
    </ExpandableCard>
  );
}
